// sort config
import { Observable } from 'rxjs';

export type SortDirections = 'asc' | 'desc';

export type TableVariant = 'light' | 'accent';

export type SortAttributes = {
  sortOrder: SortDirections;
  sortField: string;
};

export type PageAttributes = {
  index: number;
  size: number;
};

export type SearchAttributes = {
  query: string;
  category: string;
};

// chip configuration
export type ChipConfig = {
  colorScheme?: string;
  labelTranslationKey: string;
  readonly: boolean;
  icon?: string;
};

// table cell parameters
export type TableCell = {
  content?: (string | Date)[];
  contentType?: TableCellContentTypes;
  dateFormat?: TableCellDateFormat;
  label?: string;
  chips?: ChipConfig[];
  actionElements?: TableActionElement[];
  iconElements?: TableIconElement[];
  selectionPrefix?: string;
  isEditable$?: Observable<boolean>;
  isEditable?: (obj: unknown) => boolean;
  disableRowActionElementsCondition?: (obj: unknown) => boolean;
  contentIsTranslationKey?: boolean;
  truncate?: boolean;
};

// header row parameters
export type HeaderCell = {
  headerCellColumnId: string;
  headerCellTranslationKey: string;
  headerCellTitle?: string;
  isSortable?: boolean;
  sortDirection?: 'asc' | 'desc' | 'none';
};

// table row parameters
export type TableRow = {
  rowLink?: string;
  disabledRowLink?: boolean;
  disabledRow?: boolean;
  rowClickControl?: boolean;
  entryId: string;
  version: number;
  object: unknown;
  showCheckBox?: boolean;
  showRadioButton?: boolean;
  tableCells: TableCell[];
};

// object to set parameters for header and table cells
export type HeaderAndTableCellDraft = {
  tableCellTranslationPrefix?: string;
  tableCellContentKey: string;
  tableCellContentType: TableCellContentTypes;
  tableCellDateFormat?: TableCellDateFormat;
  chipConfigMap?: Record<string, { color: string; icon: string }>;
  chipConfigMapStatic?: { color: string; icon: string };
  iconElementsConfigMap?: Record<string, { colorScheme: string; icon: string; size: string }>;
  additionalChip?: AdditionalChip<unknown>;
  tableCellTruncate?: boolean;
} & HeaderCell &
  TableCell;

// show chip in a cell addition to string for the content type 'CHIP_AND_STRING'
export type AdditionalChip<T> = {
  displayCondition: (obj: T) => boolean;
  tableCellContentKey: string;
  tableCellTranslationPrefix: string;
  chipConfigMap: Record<string, { color: string; icon: string }>;
};

// object to prepare a table
export type TableDraft = {
  tableVariant?: TableVariant;
  hiddenColumns?: string[];
  disabledRowLink?: boolean; // disable click all rows
  disabledRow?: (obj: unknown) => boolean; // disable a specific row
  rowClickControl?: boolean;
  rowLink?: string[];
  showCheckBoxes?: boolean;
  isShowCheckboxForRow?: (obj: unknown) => boolean;
  showRadioButtons?: boolean;
  isShowRadioButtonForRow?: (obj: unknown) => boolean;
  sortColumnIds?: string[];
  sortStartColumnId?: string;
  sortStartDirection?: SortDirections;
  headerAndTableCells: HeaderAndTableCellDraft[];
};

// final object for rendering a table
export type MappedTableData = {
  tableVariant?: TableVariant;
  rowLink?: string[]; // Set this property to specify navigate route by clicking rows. default is "./${entryId}"
  disabledRowLink?: boolean;
  disabledRow?: boolean;
  rowClickControl?: boolean;
  showCheckBoxes: boolean;
  showRadioButtons: boolean;
  sortColumnIds: string[];
  sortStartColumnId: string;
  sortStartDirection: SortDirections;
  headerCells: HeaderCell[];
  tableRows: TableRow[];
};

export const ENTRY_ID = 'ENTRY_ID';

// data types for content cells
export type TableCellContentTypes =
  | 'STRING'
  | 'DATE'
  | 'CHIP'
  | 'NUMBER'
  | 'CHIP_AND_STRING'
  | 'ACTION'
  | 'CHIP_PREDEFINED'
  | 'ICONS'
  | 'SELECT';

export type TableCellDateFormat = 'full' | 'long' | 'medium' | 'short';

export const TableActionCell = (tableActions: TableActionElement[]): HeaderAndTableCellDraft => ({
  headerCellColumnId: 'actions',
  headerCellTranslationKey: '',
  tableCellContentKey: 'id',
  actionElements: tableActions,
  tableCellContentType: 'ACTION',
});

// a table action element is an element for user interaction inside a table cell
export type TableActionElement = {
  icon: string;
  colorScheme: string;
  dynamicIcons?: boolean;
  label?: string;
  size: string; // size in pixel
  function: (...args: unknown[]) => unknown; // generic function with generic params to attach
};

export type TableIconElement = {
  icon?: string;
  colorScheme?: string;
  size?: string;
};

export type TableParamConfig = {
  activeFilter: Record<string, string[]>;
  searchQuery: string;
  page: { index: number; size: 5 | 10 | 25 | 50 | 100 };
  sort?: SortAttributes;
  hiddenColumns?: string[];
};

export type ColumnSelectItem = {
  columnId: string;
  label: string;
};
